import { lazy } from 'react';
import { validatePayloadWithSchemaMap } from './payloadSchemas';
import { type ScreenEntry } from './types';

function readString(payload: unknown, key: string): string | null {
  if (typeof payload !== 'object' || payload === null) return null;
  const value = (payload as Record<string, unknown>)[key];
  return typeof value === 'string' && value.length > 0 ? value : null;
}

function shortId(id: string): string {
  return id.length > 10 ? `${id.slice(0, 8)}…` : id;
}

/**
 * Surface registry — maps surface keys to their metadata and component.
 * Unknown keys are resolved to FallbackSurface by resolveSurface().
 */
export const registry: Record<string, ScreenEntry> = {
  preferences: {
    kind: 'form',
    scopes: [],
    component: lazy(() => import('./forms/UserPreferences')),
    i18nNamespace: 'surface.preferences',
    defaultLayout: { w: 4, h: 8 },
  },
  bond_deal_workbook: {
    kind: 'mixed',
    scopes: ['deals:capture', 'deals:read'],
    component: lazy(() => import('./mixed/BondDealWorkbook')),
    i18nNamespace: 'surface.bond_deal_workbook',
    defaultLayout: { w: 10, h: 14 },
    breadcrumb: (payload) => {
      const id = readString(payload, 'deal_id');
      return id ? `Deal ${shortId(id)}` : '';
    },
  },
  bond_deal_amend: {
    kind: 'form',
    scopes: ['deals:amend'],
    component: lazy(() => import('./forms/BondDealAmend')),
    i18nNamespace: 'surface.bond_deal_amend',
    defaultLayout: { w: 6, h: 12 },
    breadcrumb: (payload) => {
      const id = readString(payload, 'deal_id');
      return id ? `Amend ${shortId(id)}` : '';
    },
  },
  bond_pretrade_ticket: {
    kind: 'mixed',
    scopes: ['pretrade:read'],
    component: lazy(() => import('./mixed/BondPretradeTicket')),
    i18nNamespace: 'surface.bond_pretrade_ticket',
    defaultLayout: { w: 10, h: 10 },
    supportsAskAi: true,
    breadcrumb: (payload) => {
      const instrumentId = readString(payload, 'instrument_identifier');
      return instrumentId ? `ISIN: ${instrumentId}` : '';
    },
  },
  batch_deal_capture: {
    kind: 'form',
    scopes: ['deals:capture'],
    component: lazy(() => import('./forms/batchDealCapture/BatchDealCapture')),
    i18nNamespace: 'surface.batch_deal_capture',
    // Keep batch capture full-span by default so wide spreadsheet columns use
    // the reclaimed desktop rail space instead of opening in a cramped tile.
    defaultLayout: { w: 12, h: 12 },
  },
  approval_context: {
    kind: 'detail',
    scopes: ['approvals:read'],
    component: lazy(() => import('./details/ApprovalContext')),
    i18nNamespace: 'surface.approval_context',
    defaultLayout: { w: 5, h: 9 },
  },
  portfolio_positions: {
    kind: 'report',
    scopes: ['positions:read'],
    component: lazy(() => import('./reports/PortfolioPositions')),
    i18nNamespace: 'surface.portfolio_positions',
    defaultLayout: { w: 8, h: 10 },
    breadcrumb: (payload) => {
      const code = readString(payload, 'book_code') ?? readString(payload, 'portfolio_id');
      return code ? `Book: ${shortId(code)}` : '';
    },
  },
  portfolio_summary: {
    kind: 'report',
    scopes: ['positions:read'],
    component: lazy(() => import('./reports/PortfolioSummary')),
    i18nNamespace: 'surface.portfolio_summary',
    defaultLayout: { w: 8, h: 8 },
    supportsAskAi: false,
  },
  portfolio_risk: {
    kind: 'report',
    scopes: ['risk:read'],
    component: lazy(() => import('./reports/PortfolioRisk')),
    i18nNamespace: 'surface.portfolio_risk',
    defaultLayout: { w: 8, h: 10 },
    supportsAskAi: false,
  },
  rates_desk: {
    kind: 'report',
    scopes: ['risk:read'],
    component: lazy(() => import('./reports/RatesDeskWidget')),
    i18nNamespace: 'surface.rates_desk',
    defaultLayout: { w: 4, h: 15 },
    supportsAskAi: false,
  },
  deal_list: {
    kind: 'report',
    scopes: ['deals:read'],
    component: lazy(() => import('./reports/DealList')),
    i18nNamespace: 'surface.deal_list',
    defaultLayout: { w: 8, h: 10 },
  },
  deal_detail: {
    kind: 'detail',
    scopes: ['deals:read'],
    component: lazy(() => import('./details/DealDetail')),
    i18nNamespace: 'surface.deal_detail',
    defaultLayout: { w: 5, h: 10 },
    breadcrumb: (payload) => {
      const id = readString(payload, 'deal_id') ?? readString(payload, 'dealId');
      return id ? `Deal ${shortId(id)}` : '';
    },
  },
  fx_spot: {
    kind: 'form',
    scopes: ['deals:capture'],
    component: lazy(() => import('./forms/FxSpot')),
    i18nNamespace: 'surface.fx_spot',
    defaultLayout: { w: 6, h: 10 },
  },
  fx_forward: {
    kind: 'form',
    scopes: ['deals:capture'],
    component: lazy(() => import('./forms/FxForward')),
    i18nNamespace: 'surface.fx_forward',
    defaultLayout: { w: 6, h: 10 },
  },
  fx_ndf: {
    kind: 'form',
    scopes: ['deals:capture'],
    component: lazy(() => import('./forms/FxNdf')),
    i18nNamespace: 'surface.fx_ndf',
    defaultLayout: { w: 6, h: 10 },
  },
  fx_option_vanilla: {
    kind: 'form',
    scopes: ['deals:capture'],
    component: lazy(() => import('./forms/FxOptionVanilla')),
    i18nNamespace: 'surface.fx_option_vanilla',
    defaultLayout: { w: 6, h: 10 },
  },
  fx_option_barrier: {
    kind: 'form',
    scopes: ['deals:capture'],
    component: lazy(() => import('./forms/FxOptionBarrier')),
    i18nNamespace: 'surface.fx_option_barrier',
    defaultLayout: { w: 6, h: 10 },
  },
  irs_vanilla: {
    kind: 'form',
    scopes: ['deals:capture'],
    component: lazy(() => import('./forms/IrsVanilla')),
    i18nNamespace: 'surface.irs_vanilla',
    defaultLayout: { w: 7, h: 10 },
  },
  irs_ois: {
    kind: 'form',
    scopes: ['deals:capture'],
    component: lazy(() => import('./forms/IrsOis')),
    i18nNamespace: 'surface.irs_ois',
    defaultLayout: { w: 7, h: 10 },
  },
  irs_basis: {
    kind: 'form',
    scopes: ['deals:capture'],
    component: lazy(() => import('./forms/IrsBasis')),
    i18nNamespace: 'surface.irs_basis',
    defaultLayout: { w: 7, h: 10 },
  },
  irs_xccy: {
    kind: 'form',
    scopes: ['deals:capture'],
    component: lazy(() => import('./forms/IrsXccy')),
    i18nNamespace: 'surface.irs_xccy',
    defaultLayout: { w: 7, h: 10 },
  },
  fra: {
    kind: 'form',
    scopes: ['deals:capture'],
    component: lazy(() => import('./forms/Fra')),
    i18nNamespace: 'surface.fra',
    defaultLayout: { w: 6, h: 10 },
  },
  swaption: {
    kind: 'form',
    scopes: ['deals:capture'],
    component: lazy(() => import('./forms/Swaption')),
    i18nNamespace: 'surface.swaption',
    defaultLayout: { w: 7, h: 10 },
  },
  repo: {
    kind: 'form',
    scopes: ['deals:capture'],
    component: lazy(() => import('./forms/Repo')),
    i18nNamespace: 'surface.repo',
    defaultLayout: { w: 7, h: 10 },
  },
  deposit: {
    kind: 'form',
    scopes: ['deals:capture'],
    component: lazy(() => import('./forms/Deposit')),
    i18nNamespace: 'surface.deposit',
    defaultLayout: { w: 6, h: 10 },
  },
  cds: {
    kind: 'form',
    scopes: ['deals:capture'],
    component: lazy(() => import('./forms/Cds')),
    i18nNamespace: 'surface.cds',
    defaultLayout: { w: 7, h: 10 },
  },
  equity_option: {
    kind: 'form',
    scopes: ['deals:capture'],
    component: lazy(() => import('./forms/EquityOption')),
    i18nNamespace: 'surface.equity_option',
    defaultLayout: { w: 6, h: 10 },
  },
  custom_product: {
    kind: 'form',
    scopes: ['deals:capture'],
    component: lazy(() => import('./forms/CustomProduct')),
    i18nNamespace: 'surface.custom_product',
    defaultLayout: { w: 7, h: 10 },
  },
  pre_trade_check: {
    kind: 'form',
    scopes: ['deals:pre-check'],
    component: lazy(() => import('./forms/PreTradeCheck')),
    i18nNamespace: 'surface.pre_trade_check',
    defaultLayout: { w: 6, h: 9 },
  },
  curve_viewer: {
    kind: 'report',
    scopes: ['indexes:read'],
    component: lazy(() => import('./reports/CurveViewer')),
    i18nNamespace: 'surface.curve_viewer',
    defaultLayout: { w: 8, h: 10 },
    supportsAskAi: false,
  },
  capture_fixing: {
    kind: 'form',
    scopes: ['fixings:capture', 'indexes:read'],
    component: lazy(() => import('./forms/CaptureFixing')),
    i18nNamespace: 'surface.capture_fixing',
    defaultLayout: { w: 6, h: 9 },
    supportsAskAi: true,
  },
  fixings_query: {
    kind: 'report',
    scopes: ['fixings:read'],
    component: lazy(() => import('./reports/FixingsQuery')),
    i18nNamespace: 'surface.fixings_query',
    defaultLayout: { w: 8, h: 9 },
    supportsAskAi: false,
  },
  market_index_manager: {
    kind: 'mixed',
    scopes: ['indexes:read'],
    component: lazy(() => import('./mixed/MarketIndexManager')),
    i18nNamespace: 'surface.market_index_manager',
    defaultLayout: { w: 9, h: 13 },
    supportsAskAi: true,
  },
  run_valuation: {
    kind: 'form',
    scopes: ['valuations:write'],
    component: lazy(() => import('./forms/RunValuation')),
    i18nNamespace: 'surface.run_valuation',
    defaultLayout: { w: 6, h: 9 },
  },
  valuation_detail: {
    kind: 'detail',
    scopes: ['valuations:read'],
    component: lazy(() => import('./details/ValuationDetail')),
    i18nNamespace: 'surface.valuation_detail',
    defaultLayout: { w: 8, h: 10 },
  },
  create_simulation: {
    kind: 'form',
    scopes: ['simulations:write'],
    component: lazy(() => import('./forms/CreateSimulation')),
    i18nNamespace: 'surface.create_simulation',
    defaultLayout: { w: 8, h: 10 },
  },
  run_simulation: {
    kind: 'form',
    scopes: ['simulations:write'],
    component: lazy(() => import('./forms/RunSimulation')),
    i18nNamespace: 'surface.run_simulation',
    defaultLayout: { w: 6, h: 8 },
  },
  simulation_results: {
    kind: 'report',
    scopes: ['simulations:read'],
    component: lazy(() => import('./reports/SimulationResults')),
    i18nNamespace: 'surface.simulation_results',
    defaultLayout: { w: 10, h: 10 },
    supportsAskAi: false,
  },
  credit_lines: {
    kind: 'report',
    scopes: ['credit:read'],
    component: lazy(() => import('./reports/CreditLines')),
    i18nNamespace: 'surface.credit_lines',
    defaultLayout: { w: 10, h: 9 },
    supportsAskAi: false,
  },
  credit_exposure: {
    kind: 'detail',
    scopes: ['credit:read'],
    component: lazy(() => import('./details/CreditExposure')),
    i18nNamespace: 'surface.credit_exposure',
    defaultLayout: { w: 8, h: 9 },
  },
  fix_cashflow: {
    kind: 'form',
    scopes: ['cashflows:fix'],
    component: lazy(() => import('./forms/FixCashflow')),
    i18nNamespace: 'surface.fix_cashflow',
    defaultLayout: { w: 6, h: 8 },
  },
  settlement_list: {
    kind: 'report',
    scopes: ['settlements:read'],
    component: lazy(() => import('./reports/SettlementList')),
    i18nNamespace: 'surface.settlement_list',
    defaultLayout: { w: 8, h: 10 },
    supportsAskAi: false,
  },
  instruct_settlement: {
    kind: 'form',
    scopes: ['settlements:instruct'],
    component: lazy(() => import('./forms/InstructSettlement')),
    i18nNamespace: 'surface.instruct_settlement',
    defaultLayout: { w: 6, h: 9 },
    supportsAskAi: true,
  },
  generate_payment: {
    kind: 'form',
    scopes: ['payments:generate'],
    component: lazy(() => import('./forms/GeneratePayment')),
    i18nNamespace: 'surface.generate_payment',
    defaultLayout: { w: 6, h: 10 },
  },
  payment_detail: {
    kind: 'detail',
    scopes: ['payments:read'],
    component: lazy(() => import('./details/PaymentDetail')),
    i18nNamespace: 'surface.payment_detail',
    defaultLayout: { w: 8, h: 10 },
  },
  execute_netting: {
    kind: 'form',
    scopes: ['netting:execute', 'netting:read', 'settlements:read'],
    component: lazy(() => import('./forms/ExecuteNetting')),
    i18nNamespace: 'surface.execute_netting',
    defaultLayout: { w: 8, h: 10 },
  },
  netting_sets: {
    kind: 'report',
    scopes: ['netting:read'],
    component: lazy(() => import('./reports/NettingSets')),
    i18nNamespace: 'surface.netting_sets',
    defaultLayout: { w: 10, h: 9 },
    supportsAskAi: false,
  },
  journal_list: {
    kind: 'report',
    scopes: ['journals:read'],
    component: lazy(() => import('./reports/JournalList')),
    i18nNamespace: 'surface.journal_list',
    defaultLayout: { w: 8, h: 10 },
    supportsAskAi: false,
  },
  journal_detail: {
    kind: 'detail',
    scopes: ['journals:read'],
    component: lazy(() => import('./details/JournalDetail')),
    i18nNamespace: 'surface.journal_detail',
    defaultLayout: { w: 7, h: 10 },
  },
  post_settlement_entry: {
    kind: 'form',
    scopes: ['journals:post'],
    component: lazy(() => import('./forms/PostSettlementEntry')),
    i18nNamespace: 'surface.post_settlement_entry',
    defaultLayout: { w: 6, h: 8 },
  },
  period_close: {
    kind: 'form',
    scopes: ['journals:period_close'],
    component: lazy(() => import('./forms/PeriodClose')),
    i18nNamespace: 'surface.period_close',
    defaultLayout: { w: 5, h: 7 },
  },
  posting_rules: {
    kind: 'report',
    scopes: ['journals:read'],
    component: lazy(() => import('./reports/PostingRules')),
    i18nNamespace: 'surface.posting_rules',
    defaultLayout: { w: 8, h: 9 },
    supportsAskAi: false,
  },
  create_instruction: {
    kind: 'form',
    scopes: ['instructions:create'],
    component: lazy(() => import('./forms/CreateInstruction')),
    i18nNamespace: 'surface.create_instruction',
    defaultLayout: { w: 6, h: 10 },
  },
  instruction_detail: {
    kind: 'detail',
    scopes: ['instructions:read'],
    component: lazy(() => import('./details/InstructionDetail')),
    i18nNamespace: 'surface.instruction_detail',
    defaultLayout: { w: 7, h: 10 },
  },
  cash_balances: {
    kind: 'report',
    scopes: ['cash:read'],
    component: lazy(() => import('./reports/CashBalances')),
    i18nNamespace: 'surface.cash_balances',
    defaultLayout: { w: 9, h: 10 },
    supportsAskAi: false,
  },
  closeout_batches: {
    kind: 'report',
    scopes: ['closeout:read'],
    component: lazy(() => import('./reports/CloseoutBatches')),
    i18nNamespace: 'surface.closeout_batches',
    defaultLayout: { w: 10, h: 10 },
    supportsAskAi: false,
  },
  matched_book: {
    kind: 'report',
    scopes: ['reports:read'],
    component: lazy(() => import('./reports/MatchedBook')),
    i18nNamespace: 'surface.matched_book',
    defaultLayout: { w: 10, h: 10 },
    supportsAskAi: false,
  },
  instrument_manager: {
    kind: 'form',
    scopes: ['instruments:create'],
    component: lazy(() => import('./forms/InstrumentManager')),
    i18nNamespace: 'surface.instrument_manager',
    defaultLayout: { w: 6, h: 10 },
    supportsAskAi: true,
  },
  instrument_list: {
    kind: 'report',
    scopes: ['instruments:read'],
    component: lazy(() => import('./reports/InstrumentList')),
    i18nNamespace: 'surface.instrument_list',
    defaultLayout: { w: 8, h: 10 },
  },
  instrument_detail: {
    kind: 'detail',
    scopes: ['instruments:read'],
    component: lazy(() => import('./details/InstrumentDetail')),
    i18nNamespace: 'surface.instrument_detail',
    defaultLayout: { w: 6, h: 10 },
  },
  instrument_events: {
    kind: 'detail',
    scopes: ['instruments:read'],
    component: lazy(() => import('./details/InstrumentEvents')),
    i18nNamespace: 'surface.instrument_events',
    defaultLayout: { w: 6, h: 10 },
  },
  deal_history: {
    kind: 'detail',
    scopes: ['deals:read'],
    component: lazy(() => import('./details/DealHistory')),
    i18nNamespace: 'surface.deal_history',
    defaultLayout: { w: 6, h: 10 },
    breadcrumb: (payload) => {
      const id = readString(payload, 'deal_id');
      return id ? `History ${shortId(id)}` : '';
    },
  },
  audit_event_history: {
    kind: 'report',
    scopes: ['audit:read'],
    component: lazy(() => import('./reports/AuditEventHistory')),
    i18nNamespace: 'surface.audit_event_history',
    defaultLayout: { w: 9, h: 10 },
    supportsAskAi: false,
  },
  verify_hash_chain: {
    kind: 'form',
    scopes: ['audit:read'],
    component: lazy(() => import('./forms/VerifyHashChain')),
    i18nNamespace: 'surface.verify_hash_chain',
    defaultLayout: { w: 6, h: 8 },
  },
  valuation_lineage: {
    kind: 'detail',
    scopes: ['audit:read'],
    component: lazy(() => import('./details/ValuationLineage')),
    i18nNamespace: 'surface.valuation_lineage',
    defaultLayout: { w: 9, h: 10 },
  },
  schema_catalog: {
    kind: 'report',
    scopes: ['config:read'],
    component: lazy(() => import('./reports/SchemaCatalog')),
    i18nNamespace: 'surface.schema_catalog',
    defaultLayout: { w: 8, h: 10 },
    supportsAskAi: false,
  },
  roles_viewer: {
    kind: 'report',
    scopes: ['config:read'],
    component: lazy(() => import('./reports/RolesViewer')),
    i18nNamespace: 'surface.roles_viewer',
    defaultLayout: { w: 10, h: 10 },
    supportsAskAi: false,
  },
  stp_rules_manager: {
    kind: 'mixed',
    scopes: ['stp:read'],
    component: lazy(() => import('./mixed/StpRulesManager')),
    i18nNamespace: 'surface.stp_rules_manager',
    defaultLayout: { w: 11, h: 16 },
  },
  approval_chains_viewer: {
    kind: 'report',
    scopes: ['config:read'],
    component: lazy(() => import('./reports/ApprovalChainsViewer')),
    i18nNamespace: 'surface.approval_chains_viewer',
    defaultLayout: { w: 10, h: 10 },
    supportsAskAi: false,
  },
  party_hierarchy: {
    kind: 'mixed',
    scopes: ['parties:internal:read'],
    component: lazy(() => import('./mixed/PartyHierarchy')),
    i18nNamespace: 'surface.party_hierarchy',
    defaultLayout: { w: 8, h: 10 },
  },
  bank_account_manager: {
    kind: 'mixed',
    scopes: ['bank_accounts:read'],
    component: lazy(() => import('./mixed/BankAccountManager')),
    i18nNamespace: 'surface.bank_account_manager',
    defaultLayout: { w: 10, h: 10 },
  },
  asset_class_extensions: {
    kind: 'report',
    scopes: ['config:read'],
    component: lazy(() => import('./reports/AssetClassExtensions')),
    i18nNamespace: 'surface.asset_class_extensions',
    defaultLayout: { w: 9, h: 10 },
    supportsAskAi: false,
  },
  calendar_manager: {
    kind: 'mixed',
    scopes: ['calendars:read'],
    component: lazy(() => import('./mixed/CalendarManager')),
    i18nNamespace: 'surface.calendar_manager',
    defaultLayout: { w: 11, h: 10 },
  },
  eod_operator: {
    kind: 'mixed',
    scopes: ['system:batch'],
    component: lazy(() => import('./mixed/EodOperator')),
    i18nNamespace: 'surface.eod_operator',
    defaultLayout: { w: 11, h: 10 },
  },
  system_status: {
    kind: 'report',
    scopes: ['system:read'],
    component: lazy(() => import('./reports/SystemStatus')),
    i18nNamespace: 'surface.system_status',
    defaultLayout: { w: 8, h: 9 },
    supportsAskAi: false,
  },
  plugin_artifacts: {
    kind: 'mixed',
    scopes: ['artifacts:read'],
    component: lazy(() => import('./mixed/PluginArtifacts')),
    i18nNamespace: 'surface.plugin_artifacts',
    defaultLayout: { w: 11, h: 10 },
  },
  jar_key_registry: {
    kind: 'form',
    scopes: ['plugins:admin'],
    component: lazy(() => import('./forms/JarKeyRegistry')),
    i18nNamespace: 'surface.jar_key_registry',
    defaultLayout: { w: 8, h: 9 },
  },

  // ── Access Administration (issues 337–343) ──────────────────────────────────
  // Each entry uses a shared placeholder until the real component ships.
  // Replace the lazy import with the surface implementation as each issue lands.
  admin_data_groups: {
    kind: 'report',
    scopes: ['entitlements:admin'],
    component: lazy(() => import('./reports/AdminDataGroups')),
    i18nNamespace: 'surface.admin_data_groups',
    defaultLayout: { w: 10, h: 12 },
  },
  admin_entitlement_grants: {
    kind: 'report',
    scopes: ['entitlements:admin'],
    component: lazy(() => import('./reports/AdminEntitlementGrants')),
    i18nNamespace: 'surface.admin_entitlement_grants',
    defaultLayout: { w: 12, h: 12 },
  },
  admin_functional_groups: {
    kind: 'report',
    scopes: ['entitlements:admin'],
    component: lazy(() => import('./reports/AdminFunctionalGroups')),
    i18nNamespace: 'surface.admin_functional_groups',
    defaultLayout: { w: 10, h: 12 },
  },
  admin_restricted_lists: {
    kind: 'report',
    scopes: ['entitlements:admin'],
    component: lazy(() => import('./reports/AdminRestrictedLists')),
    i18nNamespace: 'surface.admin_restricted_lists',
    defaultLayout: { w: 10, h: 12 },
  },
  admin_sod_conflicts: {
    kind: 'report',
    scopes: ['entitlements:admin'],
    component: lazy(() => import('./reports/AdminSodConflicts')),
    i18nNamespace: 'surface.admin_sod_conflicts',
    defaultLayout: { w: 10, h: 10 },
  },
  admin_recertification: {
    kind: 'report',
    scopes: ['entitlements:admin'],
    component: lazy(() => import('./reports/AdminRecertification')),
    i18nNamespace: 'surface.admin_recertification',
    defaultLayout: { w: 11, h: 12 },
  },

  // ── Access lifecycle workbooks (issue 360) ──────────────────────────────────
  // Consolidate the 7 flat admin surfaces above into 2 tabbed workbooks via
  // additive composition. The 7 entries above remain unchanged for chat/agent
  // deep-links; these are the primary LeftNav entry points.
  user_access_workbook: {
    kind: 'mixed',
    scopes: ['users:admin'],
    component: lazy(() => import('./mixed/UserAccessWorkbook')),
    i18nNamespace: 'surface.user_access_workbook',
    defaultLayout: { w: 10, h: 14 },
    breadcrumb: (payload) => {
      const id = readString(payload, 'user_id');
      return id ? `User ${shortId(id)}` : '';
    },
  },
  access_policy_console: {
    kind: 'mixed',
    scopes: ['entitlements:admin'],
    component: lazy(() => import('./mixed/AccessPolicyConsole')),
    i18nNamespace: 'surface.access_policy_console',
    defaultLayout: { w: 10, h: 14 },
  },
};

/**
 * Resolve a surface key to its registry entry.
 * Returns null for unknown surfaces — caller renders FallbackSurface.
 */
export function resolveSurface(key: string): ScreenEntry | null {
  return registry[key] ?? null;
}

/**
 * Validate a payload against the registered surface schema when available.
 * Surfaces without an explicit payload schema currently pass validation.
 */
export function validateSurfacePayload(surface: string, payload: unknown): boolean {
  return validatePayloadWithSchemaMap(surface, payload);
}
